import { Injectable } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  UserCredential
} from 'firebase/auth';
import { environment } from '../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private isNative = Capacitor.isNativePlatform();
  private nativeInitDone = false;

  /**
   * Single in-flight refresh so that N concurrent 401s trigger exactly one
   * token refresh (prevents the request/refresh storm that used to log the
   * user out and make the login/logout button flicker).
   */
  private refreshInFlight: Promise<string | null> | null = null;

  /**
   * Refresh token captured during the most recent Google sign-in. The backend's
   * /auth/google only echoes back an accessToken, so we hold onto the Firebase
   * refresh token here and fold it into storage after login — this is what lets
   * Google-on-iOS (which has no persisted Firebase SDK session) refresh silently.
   */
  private googleRefreshToken: string | null = null;

  constructor() {
    this.app = initializeApp(environment.firebase);
    this.auth = getAuth(this.app);
  }

  /**
   * Silently obtains a fresh Firebase ID token and persists it back to
   * localStorage so the user stays logged in past the ~1h token expiry.
   *
   * Strategy (in order):
   *  1. Firebase Web SDK session (Google web / Android) → getIdToken(true).
   *  2. A stored Firebase refresh token → securetoken.googleapis.com exchange.
   *
   * Returns the new access token, or null when no refresh is possible (caller
   * should then fall back to logging the user out).
   */
  refreshIdToken(): Promise<string | null> {
    // Coalesce concurrent callers onto a single refresh.
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<string | null> {
    try {
      // 1) Firebase SDK keeps a persisted session for SDK-based sign-ins.
      const current = this.auth.currentUser;
      if (current) {
        const fresh = await current.getIdToken(true);
        if (fresh) {
          this.persistAccessToken(fresh);
          return fresh;
        }
      }

      // 2) Fall back to a stored refresh token (issued by the backend at login).
      const refreshToken = this.readStoredRefreshToken();
      if (refreshToken) {
        const response = await fetch(
          `https://securetoken.googleapis.com/v1/token?key=${environment.firebase.apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
          }
        );
        if (response.ok) {
          const data = await response.json();
          const fresh = data.id_token || data.access_token;
          if (fresh) {
            this.persistAccessToken(fresh, data.refresh_token);
            return fresh;
          }
        } else {
          console.warn('[Auth] Refresh token exchange failed:', response.status);
        }
      }
    } catch (error) {
      console.error('[Auth] Silent token refresh failed:', error);
    }
    return null;
  }

  private readStoredRefreshToken(): string | null {
    const parse = (raw: string | null): any => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };
    const tokenParsed = parse(localStorage.getItem('token'));
    const userParsed = parse(localStorage.getItem('userDB'));
    return tokenParsed?.refreshToken || userParsed?.token?.refreshToken || null;
  }

  /**
   * Folds the refresh token captured during Google sign-in into the token that
   * the login page just stored from the /auth/google response. Call this right
   * after persisting the backend token. No-op when nothing was captured.
   */
  attachPendingGoogleRefreshToken() {
    if (!this.googleRefreshToken) return;
    this.persistToken(undefined, this.googleRefreshToken);
    this.googleRefreshToken = null;
  }

  /** Writes the freshly minted token back to both storage shapes getOpts() reads. */
  private persistAccessToken(accessToken: string, refreshToken?: string) {
    this.persistToken(accessToken, refreshToken);
  }

  private persistToken(accessToken?: string, refreshToken?: string) {
    const parse = (raw: string | null): any => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const tokenParsed = parse(localStorage.getItem('token')) || {};
    if (accessToken) tokenParsed.accessToken = accessToken;
    if (refreshToken) tokenParsed.refreshToken = refreshToken;
    localStorage.setItem('token', JSON.stringify(tokenParsed));

    const userParsed = parse(localStorage.getItem('userDB'));
    if (userParsed?.token) {
      if (accessToken) userParsed.token.accessToken = accessToken;
      if (refreshToken) userParsed.token.refreshToken = refreshToken;
      localStorage.setItem('userDB', JSON.stringify(userParsed));
    }
  }

  /** Sign in with Google; uses native plugin on mobile, popup on web */
  async signInWithGoogle(): Promise<string> {
    if (this.isNative) {
      return this.signInWithGoogleNative();
    } else {
      return this.signInWithGoogleWeb();
    }
  }

  /** Web OAuth using popup */
  private async signInWithGoogleWeb(): Promise<string> {
    const provider = new GoogleAuthProvider();
    const result: UserCredential = await signInWithPopup(this.auth, provider);
    this.googleRefreshToken = (result.user as any)?.refreshToken || null;
    const idToken = await result.user.getIdToken();
    return idToken;
  }

  /** Native OAuth using @capgo/capacitor-social-login plugin */
  private async signInWithGoogleNative(): Promise<string> {
    // Determine which client ID to use based on platform
    // Android: Use Android-specific client ID (from google-services.json client_type: 3)
    // iOS: Use iOS-specific client ID (from GoogleService-Info.plist CLIENT_ID)
    const platform = Capacitor.getPlatform();
    const clientId = platform === 'android'
      ? (environment as any).googleAndroidClientId || environment.googleClientId
      : (environment as any).googleIosClientId || environment.googleClientId;

    if (!clientId) {
      throw new Error('Google Client ID not configured.');
    }

    console.log('[Auth] Initializing Google Sign-In for platform:', platform, 'with clientId:', clientId);

    // Initialize the plugin if not already done
    if (!this.nativeInitDone) {
      try {
        await SocialLogin.initialize({
          google: {
            [platform === 'android' ? 'webClientId' : 'iOSClientId']: clientId,
            mode: 'online'
          }
        });
        this.nativeInitDone = true;
        console.log('[Auth] SocialLogin initialized successfully');
      } catch (error) {
        console.error('[Auth] Failed to initialize SocialLogin:', error);
        throw new Error(`Failed to initialize Google Sign-In: ${error}`);
      }
    }

    try {
      // Logout first to clear any cached credentials that might cause [16] error
      await SocialLogin.logout({ provider: 'google' }).catch(() => {
        // Ignore logout errors - user might not be logged in
      });
    } catch (e) {
      // Ignore - continue with login
    }

    // Perform native Google Sign-In
    const res = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['email', 'profile'],
        // Reduces error [16] account reauth failed: shows all accounts and forces new token
        filterByAuthorizedAccounts: false,
        forceRefreshToken: true,
        // Additional option to prevent silent sign-in
        prompt: 'select_account'
      }
    });

    console.log('[Auth] SocialLogin response:', JSON.stringify(res));

    if (res.provider !== 'google') {
      throw new Error('Unexpected response from Google.');
    }

    const result = res.result;
    console.log('[Auth] Result object:', JSON.stringify(result));

    // Check if offline mode (we want online)
    if (result.responseType === 'offline') {
      throw new Error('Offline mode not supported. Use online mode.');
    }

    // iOS returns authentication object, Android returns idToken directly
    const googleIdToken = result.idToken ?? (result as any).authentication?.idToken ?? null;

    console.log('[Auth] Extracted idToken:', googleIdToken ? 'present' : 'missing');

    if (!googleIdToken) {
      console.error('[Auth] No idToken in result:', result);
      throw new Error('Google token not returned. Try again.');
    }

    // Sign in to Firebase with the Google credential to get Firebase ID token
    console.log('[Auth] Exchanging Google credential for Firebase token...');
    try {
      // On iOS, use Firebase REST API for token exchange (more reliable on native)
      if (platform === 'ios') {
        console.log('[Auth] Using Firebase REST API for iOS token exchange...');
        const response = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${environment.firebase.apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              postBody: `id_token=${googleIdToken}&providerId=google.com`,
              requestUri: 'http://localhost',
              returnIdpCredential: true,
              returnSecureToken: true
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error('[Auth] Firebase REST API error:', errorData);
          throw new Error(`Firebase authentication failed: ${errorData.error?.message || 'Unknown error'}`);
        }

        const data = await response.json();
        this.googleRefreshToken = data.refreshToken || null;
        console.log('[Auth] Firebase token obtained via REST API');
        return data.idToken;
      } else {
        // Android: Use Web SDK (works fine on Android)
        const credential = GoogleAuthProvider.credential(googleIdToken, result.accessToken?.token);
        console.log('[Auth] Created credential, signing in to Firebase...');
        const firebaseResult = await signInWithCredential(this.auth, credential);
        console.log('[Auth] Firebase sign-in successful, getting Firebase ID token...');
        this.googleRefreshToken = (firebaseResult.user as any)?.refreshToken || null;
        const firebaseIdToken = await firebaseResult.user.getIdToken();
        console.log('[Auth] Firebase ID token obtained successfully');
        return firebaseIdToken;
      }
    } catch (error) {
      console.error('[Auth] Failed to exchange Google credential for Firebase token:', error);
      throw new Error(`Failed to authenticate with Firebase: ${error}`);
    }
  }
}
